import { preloadComponents, canPreload } from './lazyWithRetry';

// Critical routes that should be preloaded after initial app load
const criticalRoutes = [
  [() => import('../pages/Home/Home'), 'Home'],
  [() => import('../pages/Dashboard/Selling/SellerDashboard'), 'SellerDashboard'],
  [() => import('../pages/Dashboard/Buying/BuyerDashboard'), 'BuyerDashboard'],
  [() => import('../pages/EditListing/EditListing'), 'EditListing'],
  [() => import('../pages/Listings/Listing'), 'Listing'],
] as Array<[() => Promise<{ default: any }>, string]>;

// Secondary routes to preload if connection is good
const secondaryRoutes = [
  [() => import('../pages/Detail/Detail'), 'Detail'],
  [() => import('../pages/Listings/Listings'), 'Listings'],
  [() => import('../pages/BrowseProperties/BrowseProperties'), 'BrowseProperties'],
] as Array<[() => Promise<{ default: any }>, string]>;

/**
 * Preload critical routes after the main app has loaded
 * Uses requestIdleCallback to avoid impacting initial load performance
 */
export function preloadCriticalRoutes(): void {
  // Check if we can/should preload
  if (!canPreload()) {
    console.log('Skipping route preloading: slow connection or data saver enabled');
    return;
  }

  // Use requestIdleCallback if available, otherwise setTimeout
  const schedulePreload = (window as any).requestIdleCallback ||
    ((cb: Function) => setTimeout(cb, 1));

  // Preload critical routes first
  schedulePreload(() => {
    console.log('Starting critical route preloading...');

    preloadComponents(criticalRoutes)
      .then(() => {
        console.log('Critical routes preloaded successfully');

        // If connection is still good, preload secondary routes
        if (canPreload()) {
          schedulePreload(() => {
            console.log('Starting secondary route preloading...');
            preloadComponents(secondaryRoutes)
              .then(() => {
                console.log('Secondary routes preloaded successfully');
              })
              .catch((error) => {
                console.warn('Failed to preload some secondary routes:', error);
              });
          }, { timeout: 5000 });
        }
      })
      .catch((error) => {
        console.warn('Failed to preload some critical routes:', error);
      });
  }, { timeout: 2000 }); // Give it up to 2 seconds to start
}

/**
 * Preload a specific route on demand (e.g., on hover)
 * @param routePath - The path to the route component
 * @param routeName - Optional name for logging
 */
export function preloadRoute(
  routePath: () => Promise<{ default: any }>,
  routeName?: string
): Promise<void> {
  // Only preload if connection is good
  if (!canPreload()) {
    return Promise.resolve();
  }

  return routePath()
    .then(() => {
      if (routeName) {
        console.log(`Preloaded route: ${routeName}`);
      }
    })
    .catch((error) => {
      console.warn(`Failed to preload route ${routeName || 'unknown'}:`, error?.message);
    });
}

/**
 * Setup route preloading with delay
 * Call this after component mount to preload routes
 */
export function setupRoutePreloader(
  routes: Array<[() => Promise<{ default: any }>, string]>,
  delay = 1000
): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  // Use a timeout to avoid blocking initial render
  const timeoutId = setTimeout(() => {
    if (canPreload()) {
      preloadComponents(routes).catch(() => {
        // Silently fail - routes will load on demand
      });
    }
  }, delay);

  // Return cleanup function
  return () => clearTimeout(timeoutId);
}

// We'll use the existing browser types or provide a fallback